import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Container, Row } from "react-bootstrap";
import Section from "../UI/Section";
import Button from "../UI/Button";
import styled from "styled-components";
import { notify } from "../UI/NotificationWrapper";
import axios from "axios";
import CaptchaButton from "../Utils/Helpers/Captcha";
import I18n from "../../translations/i18n";
import UseWindowWidth from "@/hooks/UseWindowWidth";

const StyledHeroWrapper = styled.div`
  display: flex;
  padding: 120px;
  color: #FFF;

  ${({ theme }) => theme.tablet`
    justify-self: center;
    text-align: center;
    padding: 64px 24px; 
  `}
`;

const StyledHeroContent = styled.div`
  max-width: 560px;

  ${({ theme }) => theme.tablet`
    margin: 48px 0;
  `}
`;

const HeroHeader = styled.div`
  font-family: Sentient;
  font-size: 52px;
  line-height: 58px;
  letter-spacing: -1.04px;
  margin-bottom: 24px;
  font-weight: 300;
  font-feature-settings: 'salt' on, 'ss01' on;

  ${({ theme }) => theme.tablet`
    font-size: 30px;
    line-height: 36px;
    letter-spacing: -0.6px;
  `}
`;

const HeroDescription = styled.div`
  font-family: Inter, sans-serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 30px; /* 150% */
  margin-bottom: 32px;

  ${({ theme }) => `
    font-size: 16px;
    line-height: 24px;
  `}
`;

const HeroTerms = styled.div`
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px; /* 133.333% */
  letter-spacing: 0.06px;
`;

const StyledForm = styled.form`
  display: flex;
  max-width: 560px; 
  background: #FFF;
  border-radius: 999px;
  padding: 8px; 
  margin-bottom: 16px;
  align-items: center;
`;

const HeroInputWrapper = styled.div`
  display: flex;
  flex: 1;
  background: transparent;
  padding: 12px 16px;
`;

const StyledInput = styled.input`
  height: 24px;
  border: none;
  width: 100%;

  &:focus {
    outline: none; 
  }
`;

const StyledA = styled.a`
  text-decoration: underline;
  color: #ffffff;

  &:hover {
    color: #cbaf82;
  }
`;

type EnrollmentFormProps = {
  wid: string;
  setEnrollmentSuccess: (success: boolean) => void;
  captchaKey: string;
  enrollmentSource: string;
}

const EnrollmentForm = ({
  wid,
  setEnrollmentSuccess,
  captchaKey,
  enrollmentSource,
}: EnrollmentFormProps) => {
  const [disableButton, setDisableButton] = useState(false);
  const { reset, handleSubmit, register, setValue } = useForm();
  const csrf = document
    .querySelector("meta[name='csrf-token']")
    .getAttribute("content");

  const onSubmit = (data: any) => {
    setDisableButton(true);
    const valuesToAdd = {
      wid: wid,
      platform_id: 1,
      source: enrollmentSource,
      first_name: null,
      last_name: null,
      agent_email: null,
    };

    const submissionData = Object.assign(data, valuesToAdd);
    const submitData = JSON.stringify(submissionData);

    const apiConfig = {
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrf,
      },
    };

    axios
      .post(`/api/v1/accounts/sign_up`, submitData, apiConfig)
      .then((response) => {
        if (response.data.success) {
          // Show confirmation screen
          setEnrollmentSuccess(true);
          let header = "Please Check Your Email To Confirm Your Account";
          let body = "";
          reset();
          notify(header, body, "success");
        }
      })
      .catch((error) => {
        // Error - show notification
        setDisableButton(false);
        let header = "Account Already Exists For This Email";
        let body = "";
        notify(header, body, "error");
      });
  };

  const onVerifyCaptcha = (token) => {
    setValue("g-recaptcha-response", token);
    handleSubmit(onSubmit)();
  };

  useEffect(() => {
    register({ name: "g-recaptcha-response" });
  });

  return (
    <StyledForm>
      <HeroInputWrapper id="email">
        <StyledInput
          id="email"
          name="email"
          type="email"
          placeholder="email@example.com"
          ref={register({
            required: "Please provide a valid email address",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address",
            },
          })}
        />
      </HeroInputWrapper>
      <CaptchaButton
        setCaptchaToken={onVerifyCaptcha}
        captchaKey={captchaKey}
        button={
          <Button
            kind="primary"
            loading={disableButton}
            disabled={disableButton}
            inline={true}
            small={true}
            customStyling={`
              padding: 12px 24px; 
              line-height: unset; 
              height: 100%; 
              white-space: nowrap;
            `}
          >
            {I18n.t("views.static_pages.hotel_landing.hotel_hero.join_button")}
          </Button>
        }
      />
    </StyledForm>
  );
};

type HotelHeroProps = {
  wid: string;
  hotelName: string;
  hotelCover: any;
  hotelLoyalty: any;
  setEnrollmentSuccess: (success: boolean) => void;
  captchaKey: string;
  enrollmentSource: string;
}

const HotelHero = ({
  wid,
  hotelName,
  hotelCover,
  hotelLoyalty,
  setEnrollmentSuccess,
  captchaKey,
  enrollmentSource,
}: HotelHeroProps) => {
  const WindowWidth = UseWindowWidth();
  let heroImage = "";

  const toTitleCase = (str) => {
    const minorWords = ['a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor', 'on', 'at', 'to', 'from', 'by', 'in', 'of'];

    return str.toLowerCase().split(' ').map((word, index) => {
      if (index === 0 || !minorWords.includes(word)) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word;
    }).join(' ');
  }

  if (hotelCover) {
    if (WindowWidth > 767) {
      heroImage = hotelCover.desktop_url;
    } else {
      heroImage = hotelCover.mobile_url;
    }
  } else {
    heroImage = "https://theguestbook.s3.us-east-1.amazonaws.com/public/hotel_landing_hotel_hero.jpg"
  }

  const heroHeader = hotelLoyalty?.header_title ?
    toTitleCase(hotelLoyalty.header_title) :
    I18n.t("views.static_pages.hotel_landing.hotel_hero.header", {
      hotel_name: hotelName
    });

  const heroDescription = hotelLoyalty?.header_subtitle ||
    I18n.t("views.static_pages.hotel_landing.hotel_hero.description", {
      hotel_name: hotelName,
      the_guestbook: "The Guestbook",
    });

  return (
    <Row>
      <Section
        backgroundType="image"
        backgroundData={heroImage}
        isHeroImage={false}
        isHotelLanding={true}
      >
        <Container fluid>
          <StyledHeroWrapper>
            <StyledHeroContent>
              <HeroHeader>
                {heroHeader}
              </HeroHeader>
              <HeroDescription>
                {heroDescription}
              </HeroDescription>
              <EnrollmentForm
                wid={wid}
                setEnrollmentSuccess={setEnrollmentSuccess}
                captchaKey={captchaKey}
                enrollmentSource={enrollmentSource}
              />
              <HeroTerms>
                {I18n.t("views.static_pages.hotel_landing.hotel_hero.terms")}&nbsp;
                <StyledA href="/terms" target="_blank">
                  {I18n.t("views.static_pages.hotel_landing.hotel_hero.terms_and_conditions")}
                </StyledA>
                &nbsp;{I18n.t("views.static_pages.hotel_landing.hotel_hero.and")}&nbsp;
                <StyledA href="/privacy" target="_blank">
                  {I18n.t("views.static_pages.hotel_landing.hotel_hero.privacy_policy")}
                </StyledA>.
              </HeroTerms>
            </StyledHeroContent>
          </StyledHeroWrapper>
        </Container>
      </Section>
    </Row>
  )
};

export default HotelHero;
